/**
 * 在途监管平台 — 路由配置
 * 单工程双/三角色：/gov 政府端、/enterprise 企业端、/driver 司机端
 * 登录页 /login 独立于角色 Shell；受保护路由均由 AuthGuard 守卫。
 * QA#2：根路径 "/" 与未知路径 "*" 按登录主体身份分流（司机 → /driver、
 * 企业 → /enterprise、政府/其他 → /gov；未登录 → /login），复用 /login 的
 * defaultHome 判定（@/utils/roleHome），避免默认落政府端造成权限报错。
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import { GovShell, EnterpriseShell, DriverShell } from '@/components/layout/AppShell';
import AuthGuard from '@/components/AuthGuard';
import { TOKEN_KEY } from '@/api/http';
import { defaultHome } from '@/utils/roleHome';

import Login from '@/pages/Login';

import GovDashboard from '@/pages/gov/GovDashboard';
import GovVehicles from '@/pages/gov/GovVehicles';
import GovVehicleDetail from '@/pages/gov/GovVehicleDetail';
import GovAlerts from '@/pages/gov/GovAlerts';
import GovQuarantine from '@/pages/gov/GovQuarantine';
import GovArchives from '@/pages/gov/GovArchives';

import EntDashboard from '@/pages/enterprise/EntDashboard';
import EntOrders from '@/pages/enterprise/EntOrders';
import EntOrderCreate from '@/pages/enterprise/EntOrderCreate';
import EntOrderDetail from '@/pages/enterprise/EntOrderDetail';
import EntVehicles from '@/pages/enterprise/EntVehicles';
import EntDrivers from '@/pages/enterprise/EntDrivers';

import DriverHome from '@/pages/driver/DriverHome';
import DriverOrders from '@/pages/driver/DriverOrders';
import DriverOrderDetail from '@/pages/driver/DriverOrderDetail';

/** 落地页守卫：未登录 → /login；已登录 → 按主体身份分流的默认首页 */
function LandingRedirect() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return <Navigate to="/login" replace />;
  return <Navigate to={defaultHome()} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* 登录（独立页，不进入任何角色 Shell） */}
      <Route path="/login" element={<Login />} />

      {/* 政府端（需登录） */}
      <Route
        path="/gov"
        element={
          <AuthGuard>
            <GovShell />
          </AuthGuard>
        }
      >
        <Route index element={<GovDashboard />} />
        <Route path="vehicles" element={<GovVehicles />} />
        <Route path="vehicles/:id" element={<GovVehicleDetail />} />
        <Route path="alerts" element={<GovAlerts />} />
        <Route path="quarantine" element={<GovQuarantine />} />
        <Route path="archives" element={<GovArchives />} />
      </Route>

      {/* 企业端（需登录） */}
      <Route
        path="/enterprise"
        element={
          <AuthGuard>
            <EnterpriseShell />
          </AuthGuard>
        }
      >
        <Route index element={<EntDashboard />} />
        <Route path="orders" element={<EntOrders />} />
        <Route path="orders/new" element={<EntOrderCreate />} />
        <Route path="orders/:id" element={<EntOrderDetail />} />
        <Route path="vehicles" element={<EntVehicles />} />
        <Route path="drivers" element={<EntDrivers />} />
      </Route>

      {/* 司机端（需登录） */}
      <Route
        path="/driver"
        element={
          <AuthGuard>
            <DriverShell />
          </AuthGuard>
        }
      >
        <Route index element={<DriverHome />} />
        <Route path="orders" element={<DriverOrders />} />
        <Route path="orders/:id" element={<DriverOrderDetail />} />
      </Route>

      <Route path="/" element={<LandingRedirect />} />
      <Route path="*" element={<LandingRedirect />} />
    </Routes>
  );
}
